// src/context/WishlistContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('wishlistItems');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setWishlistItems(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save to localStorage
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('wishlistItems', JSON.stringify(wishlistItems));
      } catch (error) {
        console.error('Error saving wishlist:', error);
      }
    }
  }, [wishlistItems, isLoaded]);

  // ================= ADD TO WISHLIST =================
  const addToWishlist = (product) => {
    setWishlistItems(prev => {
      if (prev.some(item => item.id === product.id)) {
        return prev;
      }
      return [
        ...prev,
        {
          id: product.id,
          title: product.title,
          price: Number(product.price),
          originalPrice: Number(product.originalPrice) || 0,
          image: product.image || (product.images && product.images[0]) || '',
          category: product.category || '',
          addedAt: Date.now(),
        },
      ];
    });
  };

  // ================= REMOVE FROM WISHLIST =================
  const removeFromWishlist = (productId) => {
    setWishlistItems(prev => prev.filter(item => item.id !== productId));
  };

  // ================= TOGGLE =================
  const toggleWishlist = (product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  // ================= CHECK IF IN WISHLIST =================
  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.id === productId);
  };

  // ================= CLEAR WISHLIST =================
  const clearWishlist = () => {
    setWishlistItems([]);
  };

  // ================= COUNT =================
  const getWishlistCount = () => {
    return wishlistItems.length;
  };

  const value = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isInWishlist,
    clearWishlist,
    getWishlistCount,
    isLoaded,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export default WishlistContext;